import { useEffect, useRef, useState } from 'react';
import FilterDropdown from '../components/FilterDropdown';
import { getPetalTransactions } from '../services/paymentService';

const PAGE_SIZE = 20;

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'purchase', label: 'Purchase' },
  { value: 'send', label: 'Send' },
  { value: 'receive', label: 'Receive' },
  { value: 'trip_creation_fee', label: 'Trip Creation Fee' },
  { value: 'trip_creation_refund', label: 'Trip Creation Refund' },
  { value: 'trip_unarchive_fee', label: 'Trip Unarchive Fee' },
  { value: 'venue_coupon_purchase', label: 'Venue Coupon Purchase' },
  { value: 'action_pack_purchase', label: 'Action Pack Purchase' },
  { value: 'gift_card_redemption', label: 'Gift Card Redemption' },
  { value: 'gift_card_refund', label: 'Gift Card Refund' },
];

const DIRECTION_OPTIONS = [
  { value: '', label: 'All Directions' },
  { value: 'credit', label: 'Credit' },
  { value: 'debit', label: 'Debit' },
];

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString();
}

function humanize(value) {
  if (!value) return '—';
  const text = value.replaceAll('_', ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function getPageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items = [1];

  if (current > 3) items.push('…');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let pageNum = start; pageNum <= end; pageNum += 1) {
    items.push(pageNum);
  }

  if (current < total - 2) items.push('…');

  items.push(total);
  return items;
}

function PersonCell({ person }) {
  if (!person) return '—';
  const name = person.name || '—';

  return (
    <div className="user-cell">
      <div className="user-cell-avatar">
        <span className="avatar-placeholder">
          {name !== '—' ? name.charAt(0).toUpperCase() : '?'}
        </span>
      </div>
      <div className="user-cell-info">
        <span className="user-cell-name">{name}</span>
        <span className="user-cell-email">{person.email || ''}</span>
      </div>
    </div>
  );
}

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const scrollRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    async function loadTransactions() {
      setLoading(true);
      setError('');

      try {
        const response = await getPetalTransactions({
          page,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          type: typeFilter || undefined,
          direction: directionFilter || undefined,
          // The API rejects `to` without `from`, so default the start to the epoch.
          from: fromDate || toDate ? `${fromDate || '1970-01-01'}T00:00:00.000Z` : undefined,
          to: toDate ? `${toDate}T23:59:59.999Z` : undefined,
        });

        if (cancelled) return;

        const payload = response.data || {};
        setTransactions(Array.isArray(payload.transactions) ? payload.transactions : []);
        setSummary(payload.summary || null);
        setTotalPages(response.pagination?.totalPages || 1);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to fetch transactions.');
        setTransactions([]);
        setSummary(null);
        setTotalPages(1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTransactions();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, typeFilter, directionFilter, fromDate, toDate]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [page]);

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page || loading) return;
    setPage(nextPage);
  }

  const pageItems = getPageItems(page, totalPages);

  const summaryCards = [
    { label: 'Total Credited', value: summary?.totalCredited, sub: `${formatNumber(summary?.creditCount)} credits` },
    { label: 'Total Debited', value: summary?.totalDebited, sub: `${formatNumber(summary?.debitCount)} debits` },
    { label: 'Net Petals', value: summary?.net, sub: 'Credited minus debited' },
    {
      label: 'Transactions',
      value: summary ? (summary.creditCount || 0) + (summary.debitCount || 0) : null,
      sub: 'Matching current filters',
    },
  ];

  return (
    <div className="payments-page main-bg-top">
      <div className="stats-grid petal-txn-summary">
        {summaryCards.map((card) => (
          <div className="stat-card" key={card.label}>
            <div className="stat-info">
              <span className="stat-label">{card.label}</span>
              <span className="stat-value">{formatNumber(card.value)}</span>
              <span className="stat-sub">{card.sub}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="users-header">
        <h2>Petal Transactions</h2>
        <div className="users-filters petal-txn-filters">
          <div className="users-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, email or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <FilterDropdown
            ariaLabel="Filter by type"
            value={typeFilter}
            options={TYPE_OPTIONS}
            onChange={(next) => {
              setTypeFilter(next);
              setPage(1);
            }}
          />
          <FilterDropdown
            ariaLabel="Filter by direction"
            value={directionFilter}
            options={DIRECTION_OPTIONS}
            onChange={(next) => {
              setDirectionFilter(next);
              setPage(1);
            }}
          />
          <input
            type="date"
            className="filter-select filter-date-input"
            aria-label="From date"
            value={fromDate}
            max={toDate || undefined}
            onChange={(e) => {
              setFromDate(e.target.value);
              setPage(1);
            }}
          />
          <input
            type="date"
            className="filter-select filter-date-input"
            aria-label="To date"
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => {
              setToDate(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <div className="payments-table-scroll petal-txn-scroll" ref={scrollRef}>
        <div className="users-table-wrap">
          <table className="users-table payments-table petal-txn-table">
            <colgroup>
              <col style={{ width: '20%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '19%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>User</th>
                <th>Details</th>
                <th>Counterparty</th>
                <th>Amount</th>
                <th>Direction</th>
                <th>Balance</th>
                <th>Date & Time</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="users-empty">Loading transactions...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="7" className="users-empty">{error}</td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="users-empty">No transactions found.</td>
                </tr>
              ) : (
                transactions.map((txn) => (
                  <tr key={txn.id}>
                    <td><PersonCell person={txn.user} /></td>
                    <td className="petal-txn-title">
                      {txn.typeLabel || humanize(txn.type)}
                      {txn.title ? ` (${txn.title})` : ''}
                    </td>
                    <td><PersonCell person={txn.counterparty} /></td>
                    <td>
                      <div className="petals-cell">
                        <img
                          alt="Petals"
                          className="menu-icon-img"
                          src={`${process.env.PUBLIC_URL}/images/petals.png`}
                        />
                        {formatNumber(txn.amount)}
                      </div>
                    </td>
                    <td>
                      {txn.direction ? (
                        <span className={`status-badge status-${txn.direction === 'credit' ? 'success' : 'failed'}`}>
                          {humanize(txn.direction)}
                        </span>
                      ) : '—'}
                    </td>
                    <td>{formatNumber(txn.balanceBefore)} → {formatNumber(txn.balanceAfter)}</td>
                    <td>
                      <div className="datetime-cell">
                        <span className="datetime-date">{formatDate(txn.createdAt)}</span>
                        <span className="datetime-time">{formatTime(txn.createdAt)}</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && !error && totalPages > 1 && (
        <div className="payments-pagination" aria-label="Pagination">
          <button
            type="button"
            className="pagination-page-btn"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
            aria-label="Previous page"
          >
            ‹
          </button>

          {pageItems.map((item, index) =>
            item === '…' ? (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`pagination-page-btn ${page === item ? 'active' : ''}`}
                onClick={() => goToPage(item)}
                aria-current={page === item ? 'page' : undefined}
              >
                {item}
              </button>
            )
          )}

          <button
            type="button"
            className="pagination-page-btn"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
